import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Spinner,
  Input,
  makeStyles,
  tokens,
  Select,
  Field,
  MessageBar,
  MessageBarBody,
  Link,
  Switch,
  Tooltip,
} from '@fluentui/react-components';
import {
  PlayFilled,
  DismissRegular,
  ErrorCircleRegular,
  DeleteRegular,
  AddRegular,
} from '@fluentui/react-icons';
import { CopyIconButton } from '@/components/shared';
import { useWorkflowTriggers, useTriggerWorkflow } from '@/hooks/useMonitoringData';
import { type TriggerInfo, type TriggerType, type TriggerPayload, canRunBeInvokedWithPayload, canTriggerRunDraft } from '@/services/monitoringApi';
import { JsonEditor } from './JsonEditor';
import { ConnectionsDialog } from './ConnectionsDialog';
import { useTelemetry } from '@/hooks/useTelemetry';
import { isRevisionPreparing, useRevisions, applicationKeys, getLatestRevision } from '@/hooks/useApplicationManagement';
import { useQueryClient } from '@tanstack/react-query';
import type { Revision } from '@/types/automation';
import { useValidationStore } from '@/stores/validationStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { canProvideTestData, computeSchemaHash, getTriggerOutputSchema, generateSampleFromSchema, hasDynamicSchema, buildRequestTriggerSample } from '@/services/testDataService';
import { useTestDataStore } from '@/stores/testDataStore';
import { useParams } from 'react-router-dom';
import { useConnectorSwagger } from '@/hooks/useConnectorSwagger';
import { useTriggerSchemaSample } from '@/hooks/useTriggerSchemaSample';
import { useConnectionStore } from '@/stores/connectionStore';
import { getConnectorIdForAction } from '@/utils/connections';
import { useWorkflowStore } from '@/stores/workflowStore';
import { flattenValidationErrors } from '@/utils/validation';
import type { MockNodeInfo, TriggerMockPayload } from '@/types/templateMode';
import { walkAllActions } from '@/utils/workflow/walkActions';
import { doesOperationRequireConnection, extractReferenceKeyFromOperation, isConnectionReferenceComplete } from '@/services/connectionService';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '650px',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  triggerSelect: {
    width: '100%',
  },
  triggerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  triggerInfoRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  triggerInfoLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minWidth: '80px',
  },
  triggerInfoValue: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  triggerInfoUrlValue: {
    flex: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'default',
  },
  payloadSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  methodSelect: {
    width: '120px',
  },
  keyValueSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  keyValueRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  keyInput: {
    flex: 1,
    minWidth: '100px',
  },
  valueInput: {
    flex: 2,
  },
  deleteButton: {
    minWidth: 'auto',
    padding: tokens.spacingHorizontalXS,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  jsonEditor: {
    width: '100%',
  },
  textareaBody: {
    width: '100%',
    minHeight: '150px',
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
  },
  contentTypeSelect: {
    width: '150px',
  },
  noPayloadMessage: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  loadingTriggers: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
  accordion: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  errorBar: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  accordionPanel: {
    padding: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
  },
  dialogCloseButton: {
    position: 'absolute' as const,
    right: tokens.spacingHorizontalM,
    top: tokens.spacingVerticalM,
  },
  errorIcon: {
    flexShrink: 0,
    fontSize: tokens.fontSizeBase400,
  },
  mockToggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  mockToggleLabel: {
    flex: 1,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  validationErrorList: {
    margin: `${tokens.spacingVerticalXS} 0 0 0`,
    paddingLeft: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  validationErrorItem: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
  },
  validationErrorNode: {
    fontWeight: tokens.fontWeightSemibold,
    fontFamily: tokens.fontFamilyMonospace,
  },
  validationErrorToggle: {
    marginTop: tokens.spacingVerticalXS,
    paddingLeft: 0,
    minWidth: 'auto',
  },
});

type TriggerWorkflowDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (runId?: string) => void;
  /** Full trigger definitions from the workflow (type, kind, and inputs for connector identification) */
  workflowTriggers?: Record<string, { type: string; kind?: string; inputs?: Record<string, unknown> }>;
  /** Called before running to save the draft. Presence of this prop indicates draft mode. */
  onSaveDraft?: () => Promise<boolean>;
  /**
   * Submit a mock draft run for the given trigger. When provided and the global
   * mock toggle is on, the dialog runs with mock overrides instead of a live
   * draft run. The dialog saves the draft (via `onSaveDraft`) before calling this.
   */
  onMockRun?: (triggerName: string) => Promise<{ success: boolean; error?: string; runId?: string }>;
  /**
   * Optional streaming trigger callback. When provided, it is called INSTEAD of
   * the regular trigger. The streaming endpoint both triggers the run and
   * streams monitoring events via SSE.
   * Returns { success: true } if streaming started, or { success: false, error } with error message.
   */
  onStreamingDraftTrigger?: (triggerName: string, body?: unknown) => Promise<StreamingTriggerResult>;
};

/** Result from streaming trigger callback */
export type StreamingTriggerResult =
  | { success: true; runId?: string }
  | { success: false; error: string; useFallback?: boolean };

function getSubmitButtonLabel({ isSavingDraft, isPending, isPreparingRevision, isDraft, isMock }: {
  isSavingDraft: boolean;
  isPending: boolean;
  isPreparingRevision: boolean;
  isDraft: boolean;
  isMock: boolean;
}): string {
  if (isPreparingRevision) return 'Preparing workflow...';
  if (isSavingDraft) return 'Saving draft...';
  if (isPending) return 'Running...';
  if (isMock) return 'Test Draft';
  if (isDraft) return 'Test Draft';
  return 'Run';
}

/**
 * Whether the draft-mode test data editor should be shown for a given trigger type.
 * Recurrence triggers take no input — the dialog shows an explanatory note instead
 * and still allows firing the trigger via /runDraft to exercise the rest of the workflow.
 */
export const shouldShowDraftPayloadEditor = (triggerType: string): boolean =>
  triggerType !== 'Recurrence';

/**
 * Whether the workflow has at least one mock entry that will actually change
 * the run.
 *
 * A mock run hits `submitDraftRun` with a `trigger` payload and/or per-action
 * `actionOverrides` (see `triggerMockWorkflowRun`). Either of those alone is
 * enough to differentiate the request from a regular draft run, so the toggle
 * should appear when:
 *   - any action mock is enabled (`enabled !== false` AND not `runForReal`), or
 *   - the trigger mock is enabled (`enabled !== false`).
 *
 * Entries that run live (`enabled === false`, or `runForReal` on actions) are
 * effectively no-ops and don't count.
 */
export const hasEnabledMocks = (mockedNodes: Record<string, unknown>): boolean =>
  Object.values(mockedNodes).some((m) => {
    if (!m || typeof m !== 'object') return false;
    if ('actionName' in m) {
      const info = m as MockNodeInfo;
      return !info.runForReal && info.enabled !== false;
    }
    // Trigger payload entry: enabled by default, opt-out via `enabled: false`.
    const trigger = m as TriggerMockPayload;
    return trigger.enabled !== false;
  });

/** True when the action mock entry would actually replace the live call. */
const isActionMockActive = (entry: unknown): boolean => {
  if (!entry || typeof entry !== 'object' || !('actionName' in entry)) return false;
  const info = entry as MockNodeInfo;
  return !info.runForReal && info.enabled !== false;
};

/**
 * Returns true when the node has a connection reference whose target entry is
 * actually configured. Mere presence of a reference key is not enough — template
 * apply writes placeholder shells (empty `connection.id`, no `parameterValues`,
 * no `authentication`) that we must treat as missing. `isConnectionReferenceComplete`
 * is the same check used across the Connections panel and template flows.
 */
export const hasResolvedConnection = (
  nodeId: string,
  mapping: Record<string, string | null | undefined>,
  references: Record<string, unknown>,
  fallbackRefKey?: string | null,
): boolean => {
  const mappedKey = mapping[nodeId];
  const refKey = (typeof mappedKey === 'string' && mappedKey.length > 0)
    ? mappedKey
    : (typeof fallbackRefKey === 'string' && fallbackRefKey.length > 0 ? fallbackRefKey : undefined);
  if (!refKey) return false;
  const reference = (references as Record<string, Parameters<typeof isConnectionReferenceComplete>[0]>)[refKey];
  return isConnectionReferenceComplete(reference);
};

/** Number of validation errors shown collapsed before the "Show all" toggle. */
const VALIDATION_ERROR_PREVIEW_LIMIT = 5;

const getTriggerTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    Request: 'HTTP Request',
    Manual: 'Manual',
    Recurrence: 'Recurrence (Schedule)',
    ApiConnection: 'API Connection',
    ApiConnectionWebhook: 'Webhook',
  };
  return labels[type] ?? type;
};

export const TriggerWorkflowDialog = ({
  open,
  onClose,
  onSuccess,
  workflowTriggers,
  onSaveDraft,
  onMockRun,
  onStreamingDraftTrigger,
}: TriggerWorkflowDialogProps) => {
  const styles = useStyles();
  const { data: apiTriggers, isLoading: isLoadingTriggers } = useWorkflowTriggers();
  const triggerMutation = useTriggerWorkflow();
  const queryClient = useQueryClient();
  const { trackEvent } = useTelemetry();
  const { workflowName, projectName, appName } = useParams<{
    workflowName: string;
    projectName: string;
    appName: string;
  }>();
  const isDraftMode = !!onSaveDraft;
  // Gate Test Draft on the latest ACA revision being ready. The backend spins up a
  // new revision after a draft save; running before it's healthy yields 5xx/stale runs.
  const { data: revisions } = useRevisions(projectName ?? '', appName ?? '', {
    enabled: open && isDraftMode && !!projectName && !!appName,
  });
  const isPreparingRevision = useMemo(
    () => isDraftMode && isRevisionPreparing(revisions),
    [isDraftMode, revisions],
  );
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isConnectionsOpen, setIsConnectionsOpen] = useState(false);
  const hasValidated = useValidationStore((s) => s.hasValidated);
  const nodeErrors = useValidationStore((s) => s.nodeErrors);
  const globalErrors = useValidationStore((s) => s.globalErrors);
  const validationErrors = useMemo(
    () => flattenValidationErrors(nodeErrors, globalErrors),
    [nodeErrors, globalErrors],
  );
  const validationErrorCount = validationErrors.length;
  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Global mock toggle — read live from the workflow store so the dialog can
  // flip between a live draft run and a mock run.
  const mockedNodes = useWorkflowStore((s) => s.mockedNodes);
  const mockRunEnabledFromStore = useWorkflowStore((s) => s.mockRunEnabled);
  const setMockRunEnabledInStore = useWorkflowStore((s) => s.setMockRunEnabled);
  // True when at least one mocked node has its mock active (enabled, not runForReal).
  const hasMocks = useMemo(() => hasEnabledMocks(mockedNodes), [mockedNodes]);

  const [mockRunEnabled, setMockRunEnabled] = useState<boolean>(mockRunEnabledFromStore !== undefined ? mockRunEnabledFromStore : hasMocks);

  useEffect(() => {
    if (mockRunEnabledFromStore !== undefined || hasMocks !== undefined) {
      setMockRunEnabled(mockRunEnabledFromStore !== undefined ? mockRunEnabledFromStore : hasMocks);
    }
  }, [mockRunEnabledFromStore, hasMocks]);

  const useMockRun = useMemo(() => isDraftMode && hasMocks && mockRunEnabled, [isDraftMode, hasMocks, mockRunEnabled]);

  // Detect operations whose connection is missing. A node is valid when its
  // mock will replace the live call (mock toggle on AND its mock entry is
  // active), or when it has a resolved connection. Triggers and actions that
  // don't require a connection (Compose, If, Foreach, Http, …) are skipped.
  const workflowDefinition = useWorkflowStore((s) => s.workflow);
  const connectionMapping = useConnectionStore((s) => s.mapping);
  const connectionReferences = useConnectionStore((state) => state.references);
  const invalidConnectionNodeIds = useMemo<string[]>(() => {
    const triggers = workflowDefinition?.definition?.triggers;
    const actions = workflowDefinition?.definition?.actions;
    if (!triggers && !actions) return [];

    const isMocked = (nodeId: string): boolean => {
      if (!useMockRun) return false;
      const entry = mockedNodes[nodeId];
      if (!entry) return false;
      // Trigger payload entries (no `actionName`) count as mocked unless the
      // user has explicitly toggled them off on the Mock Data tab.
      if (typeof entry === 'object' && entry !== null && !('actionName' in entry)) {
        return (entry as { enabled?: boolean }).enabled !== false;
      }
      return isActionMockActive(entry);
    };

    const ids: string[] = [];

    if (triggers) {
      for (const [name, trigger] of Object.entries(triggers)) {
        const type = (trigger as { type?: string })?.type ?? '';
        if (!doesOperationRequireConnection(type)) continue;
        const nodeId = `trigger-${name}`;
        if (isMocked(nodeId)) continue;
        const fallbackRefKey = extractReferenceKeyFromOperation(trigger as Record<string, unknown>);
        if (!hasResolvedConnection(nodeId, connectionMapping, connectionReferences, fallbackRefKey)) {
          ids.push(nodeId);
        }
      }
    }

    if (actions) {
      walkAllActions(actions as Record<string, unknown>, (_names, action, _scopedName, nodeId) => {
        const type = (action as { type?: string })?.type ?? '';
        if (!doesOperationRequireConnection(type)) return;
        if (isMocked(nodeId)) return;
        const fallbackRefKey = extractReferenceKeyFromOperation(action as Record<string, unknown>);
        if (!hasResolvedConnection(nodeId, connectionMapping, connectionReferences, fallbackRefKey)) {
          ids.push(nodeId);
        }
      });
    }

    return ids;
  }, [workflowDefinition, mockedNodes, useMockRun, connectionMapping, connectionReferences]);

  const handleMockToggleChange = useCallback((_ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    setMockRunEnabled(data.checked);
    setMockRunEnabledInStore(data.checked);
  }, [setMockRunEnabledInStore]);
  
  // Build trigger list. In draft mode, the workflow definition is the source of truth
  // for which triggers exist (the canvas). The runtime API may have stale data from
  // the last published version. We use workflowTriggers as primary and enrich from
  // apiTriggers for runtime-only info (callback URL, method).
  const triggers = useMemo((): TriggerInfo[] | undefined => {
    // Primary: workflow definition triggers (what's actually on the canvas)
    if (workflowTriggers && Object.keys(workflowTriggers).length > 0) {
      const apiTriggerMap = new Map(
        (apiTriggers ?? []).map(t => [t.name, t])
      );
      return Object.entries(workflowTriggers).map(([name, trigger]): TriggerInfo => {
        const triggerType = trigger.type;
        const triggerKind = trigger.kind;
        const acceptsPayload = ['Request', 'HttpWebhook', 'Manual'].includes(triggerType);
        const apiTrigger = apiTriggerMap.get(name);
        return {
          name,
          type: triggerType as TriggerType,
          acceptsPayload,
          ...(triggerKind ? { kind: triggerKind } : {}),
          // Enrich with runtime data when available (callback URL, method)
          ...(apiTrigger?.callbackUrl ? { callbackUrl: apiTrigger.callbackUrl } : {}),
          ...(apiTrigger?.method ? { method: apiTrigger.method } : {}),
        };
      });
    }

    // Fallback: use API triggers when no definition is available
    if (apiTriggers && apiTriggers.length > 0) {
      return apiTriggers;
    }

    if (!apiTriggers) return undefined;
    return apiTriggers;
  }, [apiTriggers, workflowTriggers]);

  // Basic state
  const [selectedTriggerName, setSelectedTriggerName] = useState<string>('');
  const [triggerError, setTriggerError] = useState<string>('');

   // Payload state — draft mode uses testData JSON, published mode uses full payload fields
  const [testData, setTestData] = useState<string>('{\n  \n}');
  const [testDataError, setTestDataError] = useState<string>('');
  const getPayload = useTestDataStore((s) => s.getPayload);
  const setPayloadToStore = useTestDataStore((s) => s.setPayload);
  const currentSchemaHash = useRef<string>('0');
  const userHasEdited = useRef(false);
  const lastAutoFilledTrigger = useRef<string>('');

  // Published mode payload state (full customization for Request triggers)
  const [body, setBody] = useState<string>('{\n  \n}');
  const [bodyError, setBodyError] = useState<string>('');
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);
  const [queries, setQueries] = useState<{ key: string; value: string }[]>([]);
  // Get the selected trigger info
  const selectedTrigger = useMemo((): TriggerInfo | undefined => {
    if (!triggers || !selectedTriggerName) return undefined;
    return triggers.find((t) => t.name === selectedTriggerName);
  }, [triggers, selectedTriggerName]);

  // Extract connectorId for swagger schema pre-fill (ApiConnection triggers only)
  const selectedTriggerConnectorId = useMemo(() => {
    if (!selectedTrigger || !canProvideTestData(selectedTrigger.type)) return null;
    const triggerDef = workflowTriggers?.[selectedTrigger.name];
    return triggerDef ? getConnectorIdForAction(triggerDef, connectionReferences) : null;
  }, [selectedTrigger, workflowTriggers, connectionReferences]);

  const { data: swaggerResult, isLoading: isSwaggerLoading } = useConnectorSwagger(selectedTriggerConnectorId);

  // Resolve the swagger operationId for the selected trigger. We need this
  // outside the effect so the schema-sample hook (below) can run as soon as
  // the swagger is available, in parallel with the heuristic sample generator
  // path inside the effect.
  const selectedTriggerOperationId = useMemo<string | null>(() => {
    if (!selectedTrigger || !swaggerResult) return null;
    const triggerDef = workflowTriggers?.[selectedTrigger.name];
    const inputs = triggerDef?.inputs;
    const host = inputs?.host as Record<string, unknown> | undefined;
    if (typeof host?.operationId === 'string') {
      return host.operationId;
    }
    if (typeof inputs?.path === 'string' && swaggerResult.parser) {
      const triggerMethod = (inputs?.method as string)?.toLowerCase() ?? 'get';
      return swaggerResult.parser.resolveOperationIdFromFetchPath(inputs.path as string, triggerMethod) ?? null;
    }
    return null;
  }, [selectedTrigger, swaggerResult, workflowTriggers]);

  // Pre-generated trigger output sample from Otto's `schemaSamples` catalog.
  // Returns null when no sample exists for this connector/operation — we fall
  // back to `generateSampleFromSchema` (heuristic) below in that case.
  const { data: schemaSample } = useTriggerSchemaSample({
    connectorId: selectedTriggerConnectorId,
    operationId: selectedTriggerOperationId,
    triggerType: selectedTrigger?.type,
  });

  // Auto-select first trigger when triggers load, or reset if selection is stale
  useEffect(() => {
    if (triggers && triggers.length > 0) {
      const selectionValid = triggers.some(t => t.name === selectedTriggerName);
      if (!selectedTriggerName || !selectionValid) {
        const firstTrigger = triggers[0];
        if (firstTrigger) {
          setSelectedTriggerName(firstTrigger.name);
        }
      }
    }
  }, [triggers, selectedTriggerName]);

  // Load persisted test data when trigger changes (all triggers, not just ApiConnection)
  useEffect(() => {
    if (selectedTrigger && workflowName) {
      if (userHasEdited.current && lastAutoFilledTrigger.current === selectedTrigger.name) {
        return;
      }

      if (lastAutoFilledTrigger.current !== selectedTrigger.name) {
        userHasEdited.current = false;
        lastAutoFilledTrigger.current = selectedTrigger.name;
      }

      currentSchemaHash.current = '0';
      const triggerMock = mockedNodes[`trigger-${selectedTrigger.name}`] as { body?: unknown; enabled?: boolean } | undefined;
      if (mockRunEnabled && triggerMock?.body !== undefined && triggerMock.enabled !== false) {
        setTestData(JSON.stringify(triggerMock.body, null, 2));
      } else {
        const stored = getPayload(workflowName, selectedTrigger.name);
        if (stored) {
          currentSchemaHash.current = stored.schemaHash;
          // Pretty-print persisted JSON for readability
          try {
            const formatted = JSON.stringify(JSON.parse(stored.payload), null, 2);
            setTestData(formatted);
          } catch {
            setTestData(stored.payload);
          }
        } else if (canProvideTestData(selectedTrigger.type)) {
          // ApiConnection triggers: prefer the curated Otto schemaSamples catalog
          // first; fall back to the heuristic `generateSampleFromSchema` only
          // when no curated sample is available for this connector/operation.
          if (isSwaggerLoading) {
            // Swagger is still loading — show placeholder, will re-run when loaded
            setTestData('{\n  \n}');
          } else if (schemaSample) {
            // Curated catalog sample — use as-is, no heuristic processing needed.
            // Schema hash is left at '0' because we don't have a structured
            // schema to hash here; the sample is the source of truth.
            setTestData(JSON.stringify(schemaSample, null, 2));
          } else if (swaggerResult) {
            // No curated sample — generate one from the swagger schema.
            const operationId = selectedTriggerOperationId ?? undefined;
            if (operationId && swaggerResult.swagger?.paths) {
              // Check for dynamic schema (runtime-resolved, can't pre-fill statically)
              const isDynamic = hasDynamicSchema(
                swaggerResult.swagger.paths as Record<string, unknown>,
                operationId,
                selectedTrigger.type,
              );
              if (isDynamic) {
                // Dynamic schema — can't pre-fill, user provides their own data
                setTestData('{\n  \n}');
              } else {
                const fields = getTriggerOutputSchema(
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- testDataService API uses Record<string, any>
                  swaggerResult.swagger.paths as Record<string, any>,
                  operationId,
                  selectedTrigger.type,
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- testDataService API uses Record<string, any>
                  swaggerResult.swagger.definitions as Record<string, any>,
                );
                if (fields.length > 0) {
                  currentSchemaHash.current = computeSchemaHash(fields);
                  const sample = generateSampleFromSchema(fields);
                  setTestData(JSON.stringify(sample, null, 2));
                } else {
                  setTestData('{\n  \n}');
                }
              }
            } else {
              setTestData('{\n  \n}');
            }
          } else {
            // No swagger available (no connectorId or fetch failed)
            setTestData('{\n  \n}');
          }
        } else {
          // Non-ApiConnection triggers: check for recipe-provided trigger sample first
          if (triggerMock?.body !== undefined) {
            setTestData(JSON.stringify(triggerMock.body, null, 2));
          } else {
            // Request/HTTP triggers: generate a sample body from the trigger's
            // request schema (`inputs.schema`) so the editor isn't empty.
            const triggerDef = workflowTriggers?.[selectedTrigger.name];
            const requestSchema = triggerDef?.inputs?.schema as Record<string, unknown> | undefined;
            const { sample, hasContent, hash } = buildRequestTriggerSample(requestSchema);
            if (hasContent) {
              currentSchemaHash.current = hash;
              setTestData(JSON.stringify(sample, null, 2));
            } else {
              setTestData('{\n  \n}');
            }
          }
        }
      }
      setTestDataError('');
    }
  }, [selectedTrigger, workflowName, getPayload, swaggerResult, isSwaggerLoading, workflowTriggers, schemaSample, selectedTriggerOperationId, open, mockedNodes, mockRunEnabled]);

  const resetState = useCallback(() => {
    setTriggerError('');
    setIsSavingDraft(false);
    setTestData('{\n  \n}');
    setTestDataError('');
    setBody('{\n  \n}');
    setBodyError('');
    setHeaders([]);
    setQueries([]);
    userHasEdited.current = false;
    lastAutoFilledTrigger.current = '';
  }, []);

  const completeRunTrigger = useCallback((runId?: string) => {
    onSuccess?.(runId);
    onClose();
    resetState();
    setIsRunningTrigger(false);
  }, [onSuccess, onClose, resetState]);

  const [isRunningTrigger, setIsRunningTrigger] = useState(false);
  const handleTrigger = useCallback(async () => {
    if (!selectedTrigger) return;

    setTriggerError('');

    // In draft mode, check if the trigger type supports draft run
    if (!!onSaveDraft && !canTriggerRunDraft(selectedTrigger.type)) {
      setTriggerError(`Trigger type "${selectedTrigger.type}" does not support draft run. Publish the workflow and trigger it directly.`);
      return;
    }

    // In draft mode, save the draft first
    if (onSaveDraft) {
      setIsSavingDraft(true);
      try {
        const saveSuccess = await onSaveDraft();
        if (!saveSuccess) {
          setTriggerError('Failed to save draft before running');
          setIsSavingDraft(false);
          return;
        }
        // Short delay after save to allow the backend runtime to pick up the
        // new draft files. Without this, the run trigger can race against file
        // processing and fail with 5xx — especially after template apply when
        // the workflow definition changes significantly.
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        setTriggerError('Failed to save draft: ' + (error instanceof Error ? error.message : 'Unknown error'));
        setIsSavingDraft(false);
        return;
      }
      setIsSavingDraft(false);
    }

    setIsRunningTrigger(true);

    // Global mock run — submit mock overrides instead of a live draft run.
    // The draft was already saved above; the mock-run handler reads the mock
    // map from the workflow store.
    if (useMockRun && onMockRun) {
      const result = await onMockRun(selectedTrigger.name);
      if (result.success) {
        trackEvent('execution.triggered', { workflowName, mock: 'true' });
        return completeRunTrigger(result.runId);
      }
      trackEvent('execution.trigger_failed', { error: result.error ?? 'Failed to run with mocks' });
      setTriggerError(result.error ?? 'Failed to run with mocks');
      setIsRunningTrigger(false);
      return;
    }

    // Build payload
    let payload: TriggerPayload | undefined;
    const isDraft = !!onSaveDraft;
    const isFullPayload = !isDraft && canRunBeInvokedWithPayload(selectedTrigger.type, selectedTrigger.kind);

    if (isFullPayload) {
      // Published Request trigger: full payload customization (method, headers, queries, body)
      const headersRecord = headers.filter(h => h.key.trim()).length > 0
        ? Object.fromEntries(headers.filter(h => h.key.trim()).map(h => [h.key, h.value]))
        : undefined;
      const queriesRecord = queries.filter(q => q.key.trim()).length > 0
        ? Object.fromEntries(queries.filter(q => q.key.trim()).map(q => [q.key, q.value]))
        : undefined;

      let parsedBody: unknown = undefined;
      if (body.trim() && body.trim() !== '{}' && body.trim() !== '{\n  \n}') {
        try {
          parsedBody = JSON.parse(body);
        } catch {
          setBodyError('Invalid JSON format');
          setIsRunningTrigger(false);
          return;
        }
      }

      payload = { method: selectedTrigger.method ?? 'POST' };
      if (headersRecord) payload.headers = headersRecord;
      if (queriesRecord) payload.queries = queriesRecord;
      if (parsedBody !== undefined) payload.body = parsedBody;
    } else if (isDraft && testData.trim() && testData.trim() !== '{}' && testData.trim() !== '{\n  \n}') {
      // Draft mode: test data JSON as body
      try {
        const parsedTestData = JSON.parse(testData);
        payload = { body: parsedTestData };
        if (workflowName && selectedTrigger) {
          setPayloadToStore(workflowName, selectedTrigger.name, {
            payload: testData,
            schemaHash: currentSchemaHash.current,
            lastUsed: Date.now(),
          });
        }
      } catch {
        setTestDataError('Invalid JSON format');
        setIsRunningTrigger(false);
        return;
      }
    }

    // Try streaming draft trigger first (combined trigger + SSE monitoring)
    // Streaming is draft-only — published runs always go through the regular trigger.
    // If streaming succeeds, it replaces the regular trigger entirely.
    // If it fails, show the error in the dialog.
    // Skip streaming when the container has no ready replicas (cold start /
    // scale-to-zero). The streaming POST goes directly to the ACA container
    // which will hang or return a CORS-blocked 503. The non-streaming trigger
    // below goes through the management API which handles cold start on its own.
    // Read fresh revision data from the query cache (not the render closure)
    // because the draft save above may have triggered a new revision.
    const freshRevisions = projectName && appName
      ? queryClient.getQueryData<Revision[]>(applicationKeys.revisions(projectName, appName))
      : undefined;
    const latestRevision = getLatestRevision(freshRevisions);
    const containerReady = !latestRevision || latestRevision.replicas.ready > 0;
    if (isDraft && onStreamingDraftTrigger && containerReady) {
      const result = await onStreamingDraftTrigger(selectedTrigger.name, payload?.body);
      if (result.success) {
        trackEvent('execution.triggered', { workflowName, streaming: 'true' });
        return completeRunTrigger(result.runId);
      }

      if (!result.useFallback) {
        // Streaming failed with an ambiguous error (e.g. CORS / "Failed to fetch") —
        // the backend may have created a run, so do NOT fall through to avoid duplicates.
        setTriggerError(result.error);
        setIsRunningTrigger(false);
        return;
      }
      // Fallback is safe — the backend explicitly rejected the request (HTTP 4xx/5xx)
      // or the streaming POST was never sent. Fall through to the non-streaming trigger.
    }

    // Draft: retry once (draft file may not be ready yet). Published: single attempt.
    const maxAttempts = isDraft ? 2 : 1;
    let lastError: string | null = null;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // Use conditional spread to only include payload if it has a value
      // (exactOptionalPropertyTypes doesn't allow payload: undefined)
      const result = await triggerMutation.mutateAsync({
        triggerName: selectedTrigger.name,
        ...(payload && { payload }),
        isDraftMode: isDraft,
      });

      if (result.success) {
        trackEvent('execution.triggered', { workflowName, isDraftMode: String(isDraft) });
        addNotification({
          type: 'success',
          title: 'Workflow run started',
          // Status-neutral: the run may have completed with a non-2xx Response
          // (e.g. a 400 branch). Success here means the run was created, not that
          // the workflow's own response was 2xx — see issue #2379. Kept caller-
          // agnostic because some callers refresh a runs list rather than navigate.
          message: 'The workflow run was created',
          durationMs: 3000,
        });
        return completeRunTrigger(result.runId);
      }

      lastError = result.error ?? 'Failed to trigger workflow';

      // Draft retry: 500 errors often indicate the draft file isn't ready yet
      if (isDraft && attempt < maxAttempts - 1) {
        const delay = 500 * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }

    // All attempts exhausted
    trackEvent('execution.trigger_failed', { error: lastError ?? 'Failed to trigger workflow' });
    setTriggerError(lastError ?? 'Failed to trigger workflow');
    setIsRunningTrigger(false);
  }, [selectedTrigger, onSaveDraft, useMockRun, onMockRun, testData, body, headers, queries, onStreamingDraftTrigger, trackEvent, workflowName, completeRunTrigger, triggerMutation, addNotification, setPayloadToStore, queryClient, projectName, appName]);

  const handleClose = useCallback(() => {
    // Don't run the full resetState here: clearing testData / body / headers
    // / queries during the Fluent close animation causes the Monaco editor
    // (and the rest of the dialog content) to reflow mid-fade, which the
    // user sees as a jitter. Only clear ephemeral UI state — the auto-fill
    // effect re-runs on reopen (its `open` dep) and re-derives the editor
    // contents from persisted edit / curated catalog / heuristic.
    setTriggerError('');
    setIsSavingDraft(false);
    onClose();
  }, [onClose]);

  const canTrigger = useMemo(() => {
    if (!selectedTrigger) return false;
    if (testDataError || bodyError) return false;
    return true;
  }, [selectedTrigger, testDataError, bodyError]);

  return (
    <>
    <Dialog open={open} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogTitle>
          {useMockRun ? 'Test draft workflow' : onSaveDraft ? 'Test draft workflow' : 'Run workflow'}
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={handleClose}
            className={styles.dialogCloseButton}
            aria-label="Close"
          />
        </DialogTitle>
        <DialogBody>
          <DialogContent className={styles.content}>
            {isDraftMode && hasMocks && !!onMockRun && (
              <div className={styles.mockToggleRow}>
                <Switch
                  checked={mockRunEnabled}
                  onChange={handleMockToggleChange}
                  label={mockRunEnabled ? 'Run with test data' : 'Run draft live'}
                />
                <Text className={styles.mockToggleLabel}>
                  When on, actions with test data return their configured responses
                  instead of running live.
                </Text>
              </div>
            )}
            {isLoadingTriggers ? (
              <div className={styles.loadingTriggers}>
                <Spinner size="small" label="Loading triggers..." />
              </div>
            ) : !triggers || triggers.length === 0 ? (
              <MessageBar intent="warning">
                <MessageBarBody>No triggers found for this workflow.</MessageBarBody>
              </MessageBar>
            ) : (
              <>
                {/* Trigger Selection */}
                <Field label="Trigger">
                  <Select
                    className={styles.triggerSelect}
                    value={selectedTriggerName}
                    onChange={(_, data) => setSelectedTriggerName(data.value)}
                  >
                    {triggers.map((trigger) => (
                      <option key={trigger.name} value={trigger.name}>
                        {trigger.name} ({getTriggerTypeLabel(trigger.type)})
                      </option>
                    ))}
                  </Select>
                </Field>

                {/* Trigger Info */}
                {selectedTrigger && (
                  <div className={styles.triggerInfo}>
                    <div className={styles.triggerInfoRow}>
                      <Text className={styles.triggerInfoLabel}>Type:</Text>
                      <Text className={styles.triggerInfoValue}>
                        {getTriggerTypeLabel(selectedTrigger.type)}
                      </Text>
                    </div>
                    {selectedTrigger.callbackUrl && (
                      <div className={styles.triggerInfoRow}>
                        <Text className={styles.triggerInfoLabel}>URL:</Text>
                        <Tooltip content={selectedTrigger.callbackUrl} relationship="label" withArrow>
                          <Text
                            className={styles.triggerInfoUrlValue}
                            title={selectedTrigger.callbackUrl}
                          >
                            {selectedTrigger.callbackUrl}
                          </Text>
                        </Tooltip>
                        <CopyIconButton value={selectedTrigger.callbackUrl} label="URL" size="small" />
                      </div>
                    )}
                    {selectedTrigger.method && (
                      <div className={styles.triggerInfoRow}>
                        <Text className={styles.triggerInfoLabel}>Method:</Text>
                        <Text className={styles.triggerInfoValue}>{selectedTrigger.method}</Text>
                      </div>
                    )}
                  </div>
                )}

                {/* Payload Editor — full customization for published Request triggers, test data for draft */}
                {selectedTrigger && (
                  <div className={styles.payloadSection}>
                    {onSaveDraft ? (
                      // Draft mode: payload editor for triggers that accept input,
                      // or a short note for Recurrence (fires the trigger once).
                      shouldShowDraftPayloadEditor(selectedTrigger.type) ? (
                        <>
                          {canProvideTestData(selectedTrigger.type) && (
                            <MessageBar intent="info" layout="multiline">
                              <MessageBarBody>
                                Provide sample data that simulates what the trigger would return.
                              </MessageBarBody>
                            </MessageBar>
                          )}
                          <Field
                            label="Test data (JSON)"
                            hint="Enter the JSON payload for this test run — this will flow through your workflow actions."
                            validationState={testDataError ? 'error' : 'none'}
                            validationMessage={testDataError || null}
                          >
                            <JsonEditor
                              className={styles.jsonEditor}
                              value={testData}
                              onChange={(value: string) => {
                                setTestData(value);
                                userHasEdited.current = true;
                                if (value.trim()) {
                                  try { JSON.parse(value); setTestDataError(''); } catch { setTestDataError('Invalid JSON'); }
                                } else {
                                  setTestDataError('');
                                }
                              }}
                              hasError={!!testDataError}
                              minHeight={150}
                              maxHeight={300}
                            />
                          </Field>
                        </>
                      ) : (
                        <MessageBar intent="info" layout="multiline">
                          <MessageBarBody>
                            Recurrence triggers run on a schedule once the workflow is published.
                            Click Test Draft to run the workflow once now.
                          </MessageBarBody>
                        </MessageBar>
                      )
                    ) : canRunBeInvokedWithPayload(selectedTrigger.type, selectedTrigger.kind) ? (
                      // Published Request trigger: full payload customization
                      <>
                        {/* Headers */}
                        <Field label="Headers">
                          <div className={styles.keyValueSection}>
                            {headers.map((pair, i) => (
                              <div key={i} className={styles.keyValueRow}>
                                <Input
                                  className={styles.keyInput}
                                  placeholder="Header name"
                                  value={pair.key}
                                  onChange={(_, d) => {
                                    const next = [...headers];
                                    next[i] = { ...pair, key: d.value };
                                    setHeaders(next);
                                  }}
                                />
                                <Input
                                  className={styles.valueInput}
                                  placeholder="Value"
                                  value={pair.value}
                                  onChange={(_, d) => {
                                    const next = [...headers];
                                    next[i] = { ...pair, value: d.value };
                                    setHeaders(next);
                                  }}
                                />
                                <Button
                                  className={styles.deleteButton}
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  onClick={() => setHeaders(headers.filter((_, j) => j !== i))}
                                  aria-label="Remove header"
                                />
                              </div>
                            ))}
                            <Button
                              className={styles.addButton}
                              appearance="subtle"
                              icon={<AddRegular />}
                              onClick={() => setHeaders([...headers, { key: '', value: '' }])}
                            >
                              Add header
                            </Button>
                          </div>
                        </Field>

                        {/* Query Parameters */}
                        <Field label="Query parameters">
                          <div className={styles.keyValueSection}>
                            {queries.map((pair, i) => (
                              <div key={i} className={styles.keyValueRow}>
                                <Input
                                  className={styles.keyInput}
                                  placeholder="Parameter name"
                                  value={pair.key}
                                  onChange={(_, d) => {
                                    const next = [...queries];
                                    next[i] = { ...pair, key: d.value };
                                    setQueries(next);
                                  }}
                                />
                                <Input
                                  className={styles.valueInput}
                                  placeholder="Value"
                                  value={pair.value}
                                  onChange={(_, d) => {
                                    const next = [...queries];
                                    next[i] = { ...pair, value: d.value };
                                    setQueries(next);
                                  }}
                                />
                                <Button
                                  className={styles.deleteButton}
                                  appearance="subtle"
                                  icon={<DeleteRegular />}
                                  onClick={() => setQueries(queries.filter((_, j) => j !== i))}
                                  aria-label="Remove parameter"
                                />
                              </div>
                            ))}
                            <Button
                              className={styles.addButton}
                              appearance="subtle"
                              icon={<AddRegular />}
                              onClick={() => setQueries([...queries, { key: '', value: '' }])}
                            >
                              Add parameter
                            </Button>
                          </div>
                        </Field>

                        {/* Body */}
                        <Field
                          label="Body"
                          hint="JSON body to send with the request."
                          validationState={bodyError ? 'error' : 'none'}
                          validationMessage={bodyError || null}
                        >
                          <JsonEditor
                            className={styles.jsonEditor}
                            value={body}
                            onChange={(value: string) => {
                              setBody(value);
                              if (value.trim()) {
                                try { JSON.parse(value); setBodyError(''); } catch { setBodyError('Invalid JSON'); }
                              } else {
                                setBodyError('');
                              }
                            }}
                            hasError={!!bodyError}
                            minHeight={150}
                            maxHeight={300}
                          />
                        </Field>
                      </>
                    ) : (
                      // Published non-Request trigger: no payload needed
                      <Text className={styles.noPayloadMessage}>
                        This trigger does not accept a payload. Click Run to trigger the workflow.
                      </Text>
                    )}
                  </div>
                )}

                {/* Missing Connections Error */}
                {invalidConnectionNodeIds.length > 0 && (
                  <MessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                      {'Missing connections for some operations. '}
                      <Link
                        as="button"
                        type="button"
                        onClick={() => setIsConnectionsOpen(true)}
                      >
                        Update Connections
                      </Link>
                    </MessageBarBody>
                  </MessageBar>
                )}

                {/* Validation Warning */}
                {hasValidated && validationErrorCount > 0 && (() => {
                  const visibleErrors = showAllValidationErrors
                    ? validationErrors
                    : validationErrors.slice(0, VALIDATION_ERROR_PREVIEW_LIMIT);
                  const hasOverflow = validationErrorCount > VALIDATION_ERROR_PREVIEW_LIMIT;
                  const errorLabel = validationErrorCount === 1 ? 'error' : 'errors';
                  return (
                    <MessageBar intent="warning" layout="multiline">
                      <MessageBarBody>
                        This workflow has {validationErrorCount} validation {errorLabel} that may
                        cause the run to fail.
                        <ul className={styles.validationErrorList}>
                          {visibleErrors.map((item, i) => (
                            <li
                              key={`${item.nodeName ?? 'global'}-${i}`}
                              className={styles.validationErrorItem}
                            >
                              {item.nodeName && (
                                <span className={styles.validationErrorNode}>
                                  {item.nodeName}:{' '}
                                </span>
                              )}
                              {item.error.message}
                            </li>
                          ))}
                        </ul>
                        {hasOverflow && (
                          <Button
                            className={styles.validationErrorToggle}
                            appearance="transparent"
                            size="small"
                            onClick={() => setShowAllValidationErrors((v) => !v)}
                          >
                            {showAllValidationErrors
                              ? 'Show fewer'
                              : `Show all ${validationErrorCount} ${errorLabel}`}
                          </Button>
                        )}
                      </MessageBarBody>
                    </MessageBar>
                  );
                })()}

                {/* Revision Preparing Notice */}
                {isPreparingRevision && (
                  <MessageBar
                    intent="info"
                    icon={<Spinner size="extra-tiny" />}
                    data-testid="trigger-dialog-preparing"
                  >
                    <MessageBarBody>
                      Preparing your workflow for a test run. This usually takes a few seconds...
                    </MessageBarBody>
                  </MessageBar>
                )}

                {/* Trigger Error */}
                {triggerError && (
                  <div className={styles.errorBar}>
                    <ErrorCircleRegular className={styles.errorIcon} />
                    <span>{triggerError}</span>
                  </div>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              icon={
                isSavingDraft || triggerMutation.isPending || isRunningTrigger || isPreparingRevision
                  ? <Spinner size="tiny" />
                  : <PlayFilled />
              }
              onClick={handleTrigger}
              disabled={!canTrigger || invalidConnectionNodeIds.length > 0 || isSavingDraft || isRunningTrigger || triggerMutation.isPending || isPreparingRevision}
            >
              {getSubmitButtonLabel({ isSavingDraft, isPending: triggerMutation.isPending || isRunningTrigger, isPreparingRevision, isDraft: isDraftMode, isMock: useMockRun })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
    {isConnectionsOpen && (
      <ConnectionsDialog
        open={isConnectionsOpen}
        onClose={() => setIsConnectionsOpen(false)}
        nodeIds={invalidConnectionNodeIds}
      />
    )}
    </>
  );
};
